import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Search, X } from 'lucide-react';
import clsx from 'clsx';
import { aaiApi } from '../../services/aaiApi';
import { useActiveClient } from '../../contexts/ActiveClientContext';
import { useAsync } from '../aai/useAsync';
import type { PartnerSettingsData } from '../../types/aai';
import { Action, ErrorNote, Loading } from '../../components/aa';
import PlanAndUsage from './PlanAndUsage';
import { AppsSection, SecuritySection } from './settingsSections';
import { AiPerformanceSection, PeopleSection, RolesSection, ToolsSection } from './clientSettingsSections';
import { AccessHistoryView } from './ActivityView';
import OpeningQuestions from './OpeningQuestions';

/*
 * One client's settings as the Solution Partner sees them: the boundary of the
 * assignment, what it reaches, how the client runs AI, who is in it, what they
 * pay for, and who changed access. Read-only for the partner; the client owner
 * controls these inside their own workspace.
 *
 * Opened from the gear beside the client's name, in the same large-modal shell
 * as the partner's own Settings, so the two never mix: this one is titled with
 * the client, that one with the partner.
 */

type SectionId = 'security' | 'apps' | 'tools' | 'questions' | 'ai-performance' | 'people' | 'roles' | 'plan' | 'activity';

const SECTIONS: { id: SectionId; label: string; keywords: string }[] = [
  { id: 'security', label: 'Security & data', keywords: 'retention region privacy policy boundary' },
  { id: 'apps', label: 'Connectors', keywords: 'connectors integrations access grants catalog' },
  // The client's own tools, which is a different question from the partner's
  // app catalog above: that one is what this partner may reach, this one is
  // what the client runs on and who in their company may use it.
  { id: 'tools', label: 'Tools', keywords: 'tools apps composio catalogue stack departments skills connected' },
  { id: 'questions', label: 'Framework', keywords: 'framework intake opening questions orbit asks starter deck cards dossier' },
  { id: 'ai-performance', label: 'AI performance', keywords: 'model route evaluation fallback' },
  { id: 'people', label: 'People & departments', keywords: 'team members roster departments' },
  // { id: 'roles', label: 'Roles & access', keywords: 'permissions matrix grants' },
  { id: 'plan', label: 'Plan & usage', keywords: 'billing seats subscription quotes commercial' },
  { id: 'activity', label: 'Activity & audit', keywords: 'history log events trail access' },
];

const NEEDS_PARTNER_READ: SectionId[] = ['security', 'apps'];

const sectionFrom = (raw: string | null): SectionId => {
  const found = SECTIONS.find((s) => s.id === raw);
  if (found) return found.id;
  return 'security';
};

const Intro: React.FC<{ title: string; body: string }> = ({ title, body }) => (
  <div>
    <h2 className="m-0 text-[15px] font-bold text-ink">{title}</h2>
    <p className="m-0 mt-1 text-[13px] leading-[21px] text-ink-dim">{body}</p>
  </div>
);

const ClientSettingsModal: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Opened over a client page; closing returns there. Without an origin, the
  // client's Overview is home.
  const returnTo = (location.state as { from?: string } | null)?.from || '/partner/client';
  const [params, setParams] = useSearchParams();
  const { active, loading: clientsLoading } = useActiveClient();
  const [query, setQuery] = useState('');

  const section = sectionFrom(params.get('section'));
  const goSection = (id: SectionId) => setParams({ section: id }, { replace: true });

  const partner = useAsync<PartnerSettingsData>(
    () => aaiApi.getPartnerSettings(), 'Could not load this client’s settings.', [active?.id],
  );

  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return SECTIONS;
    return SECTIONS.filter((s) => `${s.label} ${s.keywords}`.toLowerCase().includes(needle));
  }, [query]);

  const close = () => navigate(returnTo);
  const label = SECTIONS.find((s) => s.id === section)?.label || 'Settings';
  const needsRead = NEEDS_PARTNER_READ.includes(section);

  if (clientsLoading) {
    return (
      <div className="fixed inset-0 z-[200] grid place-items-center bg-ink/25 backdrop-blur-sm">
        <div className="rounded-panel bg-white px-8"><Loading label="Opening settings" /></div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-ink/30 backdrop-blur-sm"
      onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }}
    >
      <div className="flex h-full min-h-0 w-full overflow-hidden bg-white sm:h-[min(754px,calc(100vh-2rem))] sm:w-[min(1238px,calc(100vw-2rem))] sm:rounded-panel">
        <aside className="hidden w-[268px] shrink-0 flex-col border-r border-line bg-canvas-inset px-5 pb-5 pt-6 lg:flex">
          <div className="flex items-center gap-3">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-tile bg-ink text-[13px] font-bold text-white">
              {active?.initials || '?'}
            </span>
            <div className="min-w-0">
              <div className="text-[15px] font-bold leading-tight text-ink">Client settings</div>
              <div className="truncate text-[12.5px] text-ink-dim">{active?.name || 'No client selected'}</div>
            </div>
          </div>

          <div className="mt-5">
            <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-eyebrow">
              Search settings
            </div>
            <div className="relative">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-soft" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search settings"
                className="h-11 w-full rounded-card border-2 border-ink bg-white pl-9 pr-3 text-[13px] text-ink outline-none placeholder:text-ink-soft"
              />
            </div>
          </div>

          <nav className="mt-4 flex-1 space-y-0.5 overflow-y-auto">
            {shown.map((item) => {
              const on = item.id === section;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => goSection(item.id)}
                  className={clsx(
                    'flex h-11 w-full items-center gap-3 rounded-card px-4 text-left text-[13.5px] transition-colors',
                    on && 'bg-white font-bold text-ink shadow-sm',
                    !on && 'font-medium text-ink-muted hover:bg-white/60 hover:text-ink',
                  )}
                >
                  <span className="flex-1">{item.label}</span>
                  {on && <span aria-hidden className="text-ink-soft">→</span>}
                </button>
              );
            })}
            {shown.length === 0 && (
              <p className="m-0 px-4 py-3 text-[12.5px] text-ink-dim">No setting matches that.</p>
            )}
          </nav>

          <p className="m-0 mt-4 border-t border-line pt-4 text-[12px] leading-[18px] text-ink-dim">
            Read-only for you, except the framework, which is yours. Everything else the client owner controls inside their
            own workspace.
          </p>
        </aside>

        <div className="flex min-w-0 flex-1 flex-col">
          <header className="flex shrink-0 items-start justify-between gap-4 border-b border-line px-6 py-5 md:px-8">
            <div className="min-w-0">
              <div className="truncate text-[11px] font-semibold uppercase tracking-[0.14em] text-eyebrow">
                {active?.name || 'Client'}
              </div>
              <h1 className="m-0 mt-1.5 text-[26px] font-bold leading-tight tracking-[-0.015em] text-ink">
                {label}
              </h1>
            </div>
            <button
              type="button"
              onClick={close}
              aria-label="Close client settings"
              className="grid h-10 w-10 shrink-0 place-items-center rounded-full border border-line bg-white text-ink-muted transition-colors hover:bg-canvas-inset hover:text-ink"
            >
              <X size={17} />
            </button>
          </header>

          <div className="shrink-0 border-b border-line px-6 py-3 lg:hidden">
            <select
              value={section}
              onChange={(e) => goSection(e.target.value as SectionId)}
              aria-label="Settings section"
              className="h-11 w-full rounded-act border border-line-strong bg-white px-3 text-[13px] font-semibold text-ink"
            >
              {SECTIONS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-6 py-6 md:px-8">
            {!active && <ErrorNote message="Select an assigned client to see its settings." />}
            {active && needsRead && partner.loading && <Loading label="Loading client settings" />}
            {active && needsRead && partner.error && <ErrorNote message={partner.error} />}
            {active && section === 'security' && partner.data && <SecuritySection data={partner.data} />}
            {active && section === 'apps' && partner.data && <AppsSection data={partner.data} />}
            {active && section === 'tools' && (
              <div className="space-y-4">
                <Intro
                  title="Tools"
                  body={`Every tool a connection can be held to in ${active.name}'s workspace, who it is available to, and what a skill would lose without it. Read-only: who gets a tool is the client owner's decision, taken in their own workspace.`}
                />
                <ToolsSection />
              </div>
            )}
            {active && section === 'questions' && (
              <div className="space-y-4">
                <Intro
                  title="Framework"
                  body={`The self-register questions and the intake for ${active.name}. Every client inherits your version; change it here and only this client is asked differently.`}
                />
                <div className="flex flex-wrap gap-2">
                  <Action variant="quiet" to="/partner/framework">Edit the version every new client inherits</Action>
                  <Action variant="quiet" to={`/partner/framework?tab=dossier&client_id=${encodeURIComponent(active.id)}&client_name=${encodeURIComponent(active.name)}`}>Open this client&apos;s dossier</Action>
                </div>
                <OpeningQuestions clientId={active.id} clientName={active.name} />
              </div>
            )}
            {active && section === 'ai-performance' && <AiPerformanceSection />}
            {active && section === 'people' && <PeopleSection />}
            {active && section === 'roles' && <RolesSection />}
            {active && section === 'plan' && <PlanAndUsage clientId={active.id} />}
            {active && section === 'activity' && (
              <div className="space-y-4">
                <Intro
                  title="Activity & audit"
                  body={`Who changed access, permissions, or controls in ${active.name}'s workspace. What happened in the work itself is under Client Context, Activity.`}
                />
                <AccessHistoryView clientId={active.id} clientName={active.name} layer="modal" />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientSettingsModal;
